"use strict";
// packages/shared/src/endpoints.ts
//
// Cross-platform endpoint helpers for the GraphQL proxy, used by packages/shared.
// This is imported by graphql/invoker.ts.
//
// Priority:
//   1) REACT_APP_GRAPHQLPROXY_ENDPOINT            (web build / Node env override)
//   2) EXPO_PUBLIC_GRAPHQL_PROXY_ENDPOINT         (explicit Expo override; takes precedence)
//   3) EXPO_PUBLIC_MOBILE_REGION + (EU/GLOBAL)    (mobile region selection)
//   4) window.location.origin + "/graphql"        (browser fallback; CloudFront → private AppSync)
//   5) hard fallback defaults (global then eu)
//
// Notes:
// - This file intentionally stays "dumb": it selects an endpoint string.
// - If you want region auto-detection without env vars, do it in the mobile app
//   via /where-am-i and then set EXPO_PUBLIC_GRAPHQL_PROXY_ENDPOINT accordingly
//   at build time, or store region in SecureStore and use region endpoints directly.
Object.defineProperty(exports, "__esModule", { value: true });
exports.enableConfigureAgents = exports.userTable = exports.eventTable = exports.uploadTable = exports.fmHandlerArns = exports.cognitoConfig = exports.privateAgentApiEndpoint = exports.publicAgentApiEndpoint = exports.expoGraphqlProxyGlobal = exports.expoGraphqlProxyEu = exports.expoMobileRegionHint = exports.graphqlProxyEndpoint = exports.GRAPHQL_PROXY_EU_DEFAULT = exports.GRAPHQL_PROXY_GLOBAL_DEFAULT = void 0;
exports.GRAPHQL_PROXY_GLOBAL_DEFAULT = "https://chat.heartcheckapp.com/graphql";
exports.GRAPHQL_PROXY_EU_DEFAULT = "https://chat.eu.heartcheckapp.com/graphql";
function hasDOM() {
    return typeof window !== "undefined" && typeof document !== "undefined";
}
function pickEnv(name) {
    const raw = process.env[name];
    const trimmed = raw?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : undefined;
}
function normalizeRegion(raw) {
    const v = (raw || "").trim().toLowerCase();
    if (v === "eu")
        return "eu";
    if (v === "global")
        return "global";
    return undefined;
}
function normalizeUrl(raw) {
    if (!raw)
        return undefined;
    const trimmed = raw.trim();
    if (!trimmed)
        return undefined;
    // Remove trailing slash for consistency
    return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
}
// 1) Explicit overrides from env (preferred)
const fromReactEnv = normalizeUrl(pickEnv("REACT_APP_GRAPHQLPROXY_ENDPOINT"));
const fromExpoEnv = normalizeUrl(pickEnv("EXPO_PUBLIC_GRAPHQL_PROXY_ENDPOINT"));
// 2) Mobile region selection (EU/global)
const mobileRegion = normalizeRegion(pickEnv("EXPO_PUBLIC_MOBILE_REGION"));
const mobileEu = normalizeUrl(pickEnv("EXPO_PUBLIC_GRAPHQL_PROXY_EU"));
const mobileGlobal = normalizeUrl(pickEnv("EXPO_PUBLIC_GRAPHQL_PROXY_GLOBAL"));
function pickMobileGraphqlByRegion() {
    if (!mobileRegion)
        return undefined;
    // Prefer explicitly supplied per-region URLs
    if (mobileRegion === "eu") {
        if (mobileEu)
            return mobileEu;
        // Defensive fallback: if EU url missing, fall back to global url if present
        if (mobileGlobal)
            return mobileGlobal;
        return exports.GRAPHQL_PROXY_EU_DEFAULT;
    }
    // mobileRegion === "global"
    if (mobileGlobal)
        return mobileGlobal;
    if (mobileEu)
        return mobileEu;
    return exports.GRAPHQL_PROXY_GLOBAL_DEFAULT;
}
// 3) Resolve final value
let resolvedGraphqlProxyEndpoint;
if (fromReactEnv) {
    // CRA / web build or Node env override (can be prod, staging, etc.)
    resolvedGraphqlProxyEndpoint = fromReactEnv;
}
else if (fromExpoEnv) {
    // Expo / React Native bundle: single explicit endpoint (recommended for App Store)
    resolvedGraphqlProxyEndpoint = fromExpoEnv;
}
else {
    const fromRegion = pickMobileGraphqlByRegion();
    if (fromRegion) {
        resolvedGraphqlProxyEndpoint = fromRegion;
    }
    else if (hasDOM()) {
        // Browser fallback: same-origin /graphql (works on both chat.* and chat.eu.*)
        resolvedGraphqlProxyEndpoint = `${window.location.origin}/graphql`;
    }
    else {
        // Node / tests / generic fallback
        resolvedGraphqlProxyEndpoint = exports.GRAPHQL_PROXY_GLOBAL_DEFAULT;
    }
}
/**
 * GraphQL proxy endpoint used by GraphqlQuery / invokeGraphqlProxy.
 */
exports.graphqlProxyEndpoint = resolvedGraphqlProxyEndpoint;
/**
 * Helpful exports for debugging / telemetry.
 */
exports.expoMobileRegionHint = mobileRegion;
exports.expoGraphqlProxyEu = mobileEu;
exports.expoGraphqlProxyGlobal = mobileGlobal;
// ------------------------------
// Existing Global repo exports (kept)
// ------------------------------
exports.publicAgentApiEndpoint = process.env
    .REACT_APP_PUBLICAGENTAPIENDPOINT;
exports.privateAgentApiEndpoint = process.env
    .REACT_APP_PRIVATEAGENTAPIENDPOINT;
exports.cognitoConfig = {
    region: process.env.REACT_APP_REGION,
    userPoolId: process.env.REACT_APP_COGNITOPOOL,
    userPoolClientId: process.env.REACT_APP_COGNITOCLIENTAUTH,
    userPoolDomain: process.env.REACT_APP_COGNITODOMAIN,
    userPoolRedirectSignInUri: process.env
        .REACT_APP_COGNITOREDIRECTSIGNINURL,
    userPoolRedirectSignOutUri: process.env
        .REACT_APP_COGNITOREDIRECTSIGNOUTURL,
};
exports.fmHandlerArns = [
    {
        label: "start",
        name: process.env.REACT_APP_HANDLERCLAUDESTART,
    },
    {
        label: "question-and-answer",
        name: process.env.REACT_APP_HANDLERCLAUDEQUESTIONANDANSWER,
    },
];
exports.uploadTable = process.env.REACT_APP_UPLOADSTABLE;
exports.eventTable = process.env.REACT_APP_EVENTSTABLE;
exports.userTable = process.env.REACT_APP_USERSTABLE;
exports.enableConfigureAgents = process.env.REACT_APP_ENABLECONSTRUCTINGAGENTS === "true";
